import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Copy, Check, ExternalLink, Trash2 } from 'lucide-react';

interface ShareLink {
    id: number;
    token: string;
    url: string;
    expires_at: string | null;
    access_count: number;
}

interface FileItem {
    id: number;
    name: string;
    shared: boolean;
    share_link: ShareLink | null;
}

interface ShareDialogProps {
    file: FileItem;
    onClose: () => void;
}

export default function ShareDialog({ file, onClose }: ShareDialogProps) {
    const [shareLink, setShareLink] = useState<ShareLink | null>(file.share_link);
    console.log("shareLink ", shareLink)
    const [isCreating, setIsCreating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [copied, setCopied] = useState(false);

    const createShareLink = async () => {
        setIsCreating(true);

        try {
            const response = await fetch(`/uploaded_files/${file.id}/share_links`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    'Accept': 'application/json',
                }
            });

            const data = await response.json();

            if (response.ok) {
                console.log("data.share_link ", data.share_link)
                setShareLink(data.share_link);
            } else {
                alert(data.error || 'Failed to create share link');
            }
        } catch (error) {
            console.error('Error creating share link:', error);
            alert('Failed to create share link');
        }

        setIsCreating(false);
    };

    const deleteShareLink = async () => {
        if (!shareLink) return;

        if (!confirm('Are you sure you want to remove this share link?')) return;

        setIsDeleting(true);

        try {
            const response = await fetch(`/share_links/${shareLink.id}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    'Accept': 'application/json',
                }
            });

            if (response.ok) {
                setShareLink(null);
            } else {
                alert('Failed to delete share link');
            }
        } catch (error) {
            console.error('Error deleting share link:', error);
            alert('Failed to delete share link');
        }

        setIsDeleting(false);
    };

    const copyToClipboard = async () => {
        if (!shareLink) return;

        try {
            await navigator.clipboard.writeText(shareLink.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy:', error);
        }
    };

    const openLink = () => {
        if (shareLink) {
            window.open(shareLink.url, '_blank');
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Share File</DialogTitle>
                    <DialogDescription>
                        Create a public link to share "{file.name}"
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {!shareLink ? (
                        <div className="text-center py-6">
                            <p className="text-sm text-muted-foreground mb-4">
                                This file is not currently shared
                            </p>
                            <Button
                                onClick={createShareLink}
                                disabled={isCreating}
                            >
                                {isCreating ? 'Creating...' : 'Create Share Link'}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <Label>Public Link</Label>
                                <div className="flex gap-2">
                                    <Input
                                        value={shareLink.url}
                                        readOnly
                                        className="flex-1"
                                    />
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        onClick={copyToClipboard}
                                    >
                                        {copied ? (
                                            <Check className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <Copy className="h-4 w-4" />
                                        )}
                                    </Button>
                                    <Button
                                        size="icon"
                                        variant="outline"
                                        onClick={openLink}
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2 p-3 bg-muted rounded-lg">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Access Count:</span>
                                    <span className="font-medium">{shareLink.access_count}</span>
                                </div>
                                {shareLink.expires_at && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Expires:</span>
                                        <span className="font-medium">
                                            {new Date(shareLink.expires_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-between items-center pt-2">
                                <p className="text-xs text-muted-foreground">
                                    Anyone with this link can view and download the file
                                </p>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={deleteShareLink}
                                    disabled={isDeleting}
                                >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    {isDeleting ? 'Removing...' : 'Remove'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}